import { Panel, Text } from '@pandiafi/components'
import classNames from 'classnames'
import i18next from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PaymentsByMonth } from '../../../../../common/generated/residentApiSchemas'
import {
  getMonthInTranslationKey,
  MonthNumber,
} from '../../../../utils/dateUtils'
import { hideCurrentYear, localizedMonthName } from '../../utils'
import { DifferenceTotal } from '../DifferenceTotal'
import { InvoiceTargetTables } from '../InvoiceTargetTables/InvoiceTargetTables'
import { RemittanceTable } from '../RemittanceTable'
import { TRemittance } from '../../../../../common/validations/remittance'

export type PaymentMonthVariant = 'current' | 'next' | 'future' | 'history'

interface PaymentMonthProps {
  index: number
  month: PaymentsByMonth
  remittances: TRemittance[]
  withholdings: PaymentsByMonth['withholdingsUsed']
  variant: PaymentMonthVariant
}

export const PaymentMonth: React.FC<PaymentMonthProps> = ({
  index,
  month,
  remittances,
  withholdings,
  variant,
}) => {
  const { t } = useTranslation(['payments', 'common'])
  const [year, monthNumber] = month.yearMonth.split('-')
  const monthName = localizedMonthName(monthNumber, year, i18next.language)
  const title = `${monthName} ${hideCurrentYear(year)}`.trim()
  const isCurrentMonth = variant === 'current'
  const isFutureMonth = variant === 'next' || variant === 'future'
  const showsCumulativeBalance = isCurrentMonth || isFutureMonth

  return (
    <Panel
      title={title}
      titleElement={
        isCurrentMonth ? (
          <span
            aria-label={t('payments:currentMonth')}
            role="img"
            className="ml-1 inline-block size-3 rounded-full bg-common-blue-6"
          />
        ) : null
      }
      expandable
      defaultExpanded={isCurrentMonth}
      padding="s"
      className={classNames(
        '-mx-4 tablet:-mx-6 tablet:!px-6 desktop:-mx-7 desktop:!px-7 [&_h3]:!font-bold',
        {
          'bg-common-blue-2': index % 2 === 1,
        }
      )}
    >
      <div>
        <Text
          element="h4"
          weight="bold"
          textTransform="uppercase"
          className="!mb-4 desktop:!mb-6"
        >
          {t('payments:invoices')}
        </Text>

        <InvoiceTargetTables
          paymentsByTarget={month.paymentsByTarget}
          openCreditNotes={month.openCreditNotes}
        />

        <Text
          element="h4"
          weight="bold"
          textTransform="uppercase"
          className="!mt-14 !mb-4 desktop:!mt-16 desktop:!mb-8"
        >
          {t('payments:remittances')}
        </Text>

        <RemittanceTable
          allRemittances={remittances}
          paymentOrders={month.paymentOrders}
          withholdings={withholdings}
          year={year}
          monthNumber={monthNumber}
          paymentsSums={month.paymentsByTarget.map(
            (payment) => payment.expected
          )}
        />

        {showsCumulativeBalance ? (
          <DifferenceTotal
            amount={month.monthlyDifference}
            month={t(getMonthInTranslationKey(monthNumber as MonthNumber))}
          />
        ) : null}
      </div>
    </Panel>
  )
}
